import { Box } from "@mui/system";
import { View } from "../../constants";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import Tooltip from "@mui/material/Tooltip";
import {  useState, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { IconAddressBook } from "@tabler/icons";
import { configState } from "../../../../recoil/atoms";
import AddPatientDiscounts from "./AddPatientDiscount";
import { Button, Grid, Typography } from "@mui/material";
import EditPatientDiscounts from "./EditPatientDiscount";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { DiscountApi, GetDiscountsResponseDiscounts } from "../../../../api/payment";

type Props = {  
  showDiscounts: boolean;
  PatientID: any;
};

export default function PatientDiscounts({ showDiscounts, PatientID }: Props) {
  const config = useRecoilValue(configState);

  const [rowID, setRowID] = useState<string>("");
  const [view, setView] = useState<View>(View.TABLE);
  const [editInfo, setEditInfo] = useState<any | any>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [patientDiscounts, setPatientDiscounts] = useState<GetDiscountsResponseDiscounts[]>([]);

  const { canAddDiscounts, canGetDiscounts, canEditDiscounts, canDeleteDiscounts } = useAuthorization();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatientDiscount(rowID);
    handleCloseDialog();
  };

  const getPatientDiscounts = () => {
    const Currency = new DiscountApi(config);
    Currency.getDiscounts(PatientID!)
      .then((res) => {
        setPatientDiscounts(res?.data.discounts || []);        
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const deletePatientDiscount = (id: string) => {
    const PatientDiscounts = new DiscountApi(config);
    PatientDiscounts.deleteDiscount(id)
      .then((res) => {
        getPatientDiscounts();
        toast.success(res?.data?.message);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getPatientDiscounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      name: "discount_id",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },
    {
      name: "amount",
      label: "Amount in USD",
    },
    {
      name: "created_at",
      label: "Date Created",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },

    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
          {canEditDiscounts && (
            <Tooltip title="Edit">
              <Button
                color="secondary"
                onClick={() => {
                  setView(View.EDIT);
                  setEditInfo(tableMeta.tableData[tableMeta.rowIndex]);
                }}
              >
                <EditIcon color="secondary" />
              </Button>
            </Tooltip>
          )}
          {canDeleteDiscounts && (
            <Tooltip title="Delete">
              <Button
                color="error"
                sx={{
                  ml: { xs: 0, xl: 2 },
                }}
                onClick={() => {
                  handleDelete(tableMeta.rowData[0]);
                }}
              >
                <DeleteOutlineIcon color="error" />
              </Button>
            </Tooltip>
          )}
          </>
        ),
      },
    },
  ];

  return (
    <>
      {showDiscounts ? (
        <MainCard>
           <Typography variant="h2" sx={{mb:3}} >
              Discounts
            </Typography>
          <>
            <Button
              disabled={view === View.TABLE}
              variant="outlined"
              color="secondary"
              onClick={() => {
                setView(View.TABLE);
                getPatientDiscounts();
              }}
              sx={{ mb: 2 }}
            >
              View Discounts
            </Button>

            {view === View.TABLE ? (
              <Grid>
                {canGetDiscounts && (
                <>
                  <MUIDataTable
                    data={patientDiscounts}
                    columns={columns}
                    options={{
                      filter: true,
                      viewColumns: false,
                      selectableRows: "none",
                      selectableRowsOnClick: false,
                      elevation: 0,
                      rowsPerPage: 10,
                      filterType: "dropdown",
                      enableNestedDataAccess: ".",
                      responsive: "standard",
                      expandableRows: false,
                      customToolbar: () => {
                        return (
                          canAddDiscounts && (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<IconAddressBook />}
                              onClick={() => {
                                setView(View.ADD);
                              }}
                              sx={{
                                ml: "50px",
                              }}
                            >
                              Add Discount
                            </Button>
                          )
                        );
                      },
                    }}
                    title=""
                  />
                  <ConfirmDialog
                    name={"Patient Discount"}
                    handleCloseDialog={handleCloseDialog}
                    handleConfirm={handleConfirm}
                    openDialog={openDialog}
                  />
                </>
                )}
              </Grid>
            ) : view === View.ADD ? (
              <Box>
                <AddPatientDiscounts />
              </Box>
            ) : (
              <EditPatientDiscounts initialPatientDiscountValues={editInfo} />
            )}
          </>
        </MainCard>
      ) : (
        null
      )}
    </>
  );
}
